import React, { useState, useMemo } from 'react';
import { Plus, Edit, Trash2, X, Calendar } from 'lucide-react';
import { SearchBar } from '../shared/SearchBar';
import { CustomerDropdown } from '../shared/CustomerDropdown';
import { SortableTableHeader } from '../shared/SortableTableHeader';
import { Pagination } from '../shared/Pagination';
import { FormModal } from '../shared/FormModal';
import { ConfirmModal } from '../../../framework/modals/ConfirmModal';
import { FormInput } from '../shared/FormInput';
import { FormSelect } from '../shared/FormSelect';
import { useAppointmentDetailManagement } from '../hooks/useAppointmentDetailManagement';
import { useModeratorData } from '../hooks/useModeratorData';
import { useTableSort } from '../hooks/useTableSort';
import { useModeratorPermissions } from '../hooks/useModeratorPermissions';
import type { Customer } from '../types/moderator.types';

export const AppointmentDetailsSection: React.FC = () => {
  const { appointmentDetails, customers, refetchAppointmentDetails } = useModeratorData();
  const apptDetail = useAppointmentDetailManagement(refetchAppointmentDetails);
  const { canAdd, canChange, canDelete } = useModeratorPermissions();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [newNote, setNewNote] = useState('');

  const canCreateAppointmentDetail = selectedCustomer
    ? canAdd(selectedCustomer.id)
    : customers.some((customer) => canAdd(customer.id));

  const filteredDetails = appointmentDetails.filter((detail) => {
    const matchesSearch = detail.name.toLowerCase().includes(apptDetail.searchTerm.toLowerCase());
    const matchesCustomer =
      !selectedCustomer ||
      detail.customerId === selectedCustomer.id ||
      detail.customer?.id === selectedCustomer.id;
    return matchesSearch && matchesCustomer;
  });

  const customerOptions = useMemo(
    () =>
      customers
        .filter((c) => (apptDetail.editing ? canChange(c.id) : canAdd(c.id)))
        .map((c) => ({ value: c.id, label: c.name })),
    [customers, apptDetail.editing, canAdd, canChange]
  );

  const yesNoSeeNotesOptions = useMemo(() => [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
    { value: 'See Notes', label: 'See Notes' }
  ], []);

  const addNote = () => {
    if (newNote.trim()) {
      apptDetail.setForm({
        ...apptDetail.form,
        notes: [...apptDetail.form.notes, newNote.trim()]
      });
      setNewNote('');
    }
  };

  const removeNote = (index: number) => {
    apptDetail.setForm({
      ...apptDetail.form,
      notes: apptDetail.form.notes.filter((_, i) => i !== index)
    });
  };

  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredDetails, 'name', 10);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Appointment Details Management
        </h2>
        {canCreateAppointmentDetail && (
        <button
          onClick={() => apptDetail.handleCreate(selectedCustomer?.id)}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Add Appointment Detail</span>
        </button>
        )}
      </div>

      <CustomerDropdown
        customers={customers}
        selectedCustomer={selectedCustomer}
        onSelectCustomer={(customer) => {
          setSelectedCustomer(customer);
          apptDetail.setSearchTerm('');
        }}
        helperText={
          selectedCustomer
            ? `Showing appointment details for ${selectedCustomer.name}`
            : 'Showing all appointment details'
        }
      />

      <SearchBar
        value={apptDetail.searchTerm}
        onChange={apptDetail.setSearchTerm}
        placeholder={
          selectedCustomer
            ? `Search appointment details for ${selectedCustomer.name}...`
            : 'Search appointment details...'
        }
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredDetails.length}
      />

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
              <SortableTableHeader
                field="name"
                label="Name"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="customer.name"
                label="Customer"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="canCancel"
                label="Can Cancel"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="canReschedule"
                label="Can Reschedule"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <SortableTableHeader
                field="canSchedule"
                label="Can Schedule"
                currentSortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
              <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((detail) => (
              <tr key={detail.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>{detail.name}</td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                  {detail.customer?.name || '-'}
                </td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                  {detail.canCancel === '1' || detail.canCancel === 'true' ? 'Yes' : 
                   detail.canCancel === '0' || detail.canCancel === 'false' ? 'No' : 
                   detail.canCancel || '-'}
                </td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                  {detail.canReschedule === '1' || detail.canReschedule === 'true' ? 'Yes' : 
                   detail.canReschedule === '0' || detail.canReschedule === 'false' ? 'No' : 
                   detail.canReschedule || '-'}
                </td>
                <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                  {detail.canSchedule === '1' || detail.canSchedule === 'true' ? 'Yes' : 
                   detail.canSchedule === '0' || detail.canSchedule === 'false' ? 'No' : 
                   detail.canSchedule || '-'}
                </td>
                <td className="p-3">
                  <div className="flex items-center justify-end space-x-2">
                    {canChange(detail.customerId ?? detail.customer?.id) && (
                    <button
                      onClick={() => apptDetail.handleEdit(detail)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-accent)' }}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    )}
                    {canDelete(detail.customerId ?? detail.customer?.id) && (
                    <button
                      onClick={() => apptDetail.handleDelete(detail.id)}
                      className="p-2 rounded hover:bg-opacity-10"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Appointment Detail Form Modal */}
      <FormModal
        isOpen={apptDetail.showForm}
        onClose={apptDetail.handleCancel}
        title={apptDetail.editing ? 'Edit Appointment Detail' : 'Add New Appointment Detail'}
        subtitle={apptDetail.editing ? 'Update appointment detail information and notes' : 'Create a new appointment detail configuration'}
        icon={Calendar}
        maxWidth="xl"
      >
        <form onSubmit={apptDetail.handleSubmit} className="space-y-4">
              <FormInput
                label="Name"
                required
                type="text"
                value={apptDetail.form.name}
                onChange={(e) => apptDetail.setForm({ ...apptDetail.form, name: e.target.value })}
                backgroundColor="primary"
              />
              <FormSelect
                label="Customer"
                required
                value={apptDetail.form.customerId}
                onChange={(e) => apptDetail.setForm({ ...apptDetail.form, customerId: e.target.value })}
                options={customerOptions}
                placeholder="Select a customer"
                backgroundColor="primary"
              />
              <FormSelect
                label="Can Cancel"
                value={apptDetail.form.canCancel}
                onChange={(e) => apptDetail.setForm({ ...apptDetail.form, canCancel: e.target.value })}
                options={yesNoSeeNotesOptions}
                backgroundColor="primary"
              />
              <FormSelect
                label="Can Reschedule"
                value={apptDetail.form.canReschedule}
                onChange={(e) => apptDetail.setForm({ ...apptDetail.form, canReschedule: e.target.value })}
                options={yesNoSeeNotesOptions}
                backgroundColor="primary"
              />
              <FormSelect
                label="Can Schedule"
                value={apptDetail.form.canSchedule}
                onChange={(e) => apptDetail.setForm({ ...apptDetail.form, canSchedule: e.target.value })}
                options={yesNoSeeNotesOptions}
                backgroundColor="primary"
              />
              
              {/* Notes Section */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Notes</label>
                <div className="flex space-x-2 mb-3">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                    placeholder="Add a note..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addNote();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addNote}
                    className="px-4 py-2 rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                      color: 'white'
                    }}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                {apptDetail.form.notes.length > 0 && (
                  <div className="space-y-2">
                    {apptDetail.form.notes.map((note, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between p-2 rounded" 
                        style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                      >
                        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{note}</span>
                        <button
                          type="button"
                          onClick={() => removeNote(index)}
                          className="p-1 transition-colors"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={apptDetail.handleCancel}
                  className="px-4 py-2 border rounded-lg transition-colors hover:bg-gray-50"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'var(--color-text-on-accent)'
                  }}
                  disabled={apptDetail.isCreating || apptDetail.isUpdating}
                >
                  {apptDetail.isCreating || apptDetail.isUpdating ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={apptDetail.showDeleteModal}
        onClose={apptDetail.handleCancelDelete}
        onConfirm={apptDetail.handleConfirmDelete}
        title="Delete Appointment Detail"
        message="Are you sure you want to delete this appointment detail? This action cannot be undone."
        type="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={apptDetail.isDeleting}
      />
    </div>
  );
};

